import { useState } from 'react';
import {
  Background,
  Controls,
  Handle,
  MarkerType,
  MiniMap,
  Position,
  ReactFlow,
  type Connection,
  type Edge,
  type Node,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  conversationFlowRuntimeSchema,
  type ConversationFlowRuntime,
} from '@eairlines/contracts';

type Layout = Record<string, { x: number; y: number }>;
type BuilderNodeData = {
  title: string;
  kind: 'llm' | 'end';
  prompt: string;
  entry: boolean;
  global: boolean;
  tools: number;
  transitions: number;
};
type BuilderNode = Node<BuilderNodeData, 'step'>;

function StepBox({ data, selected }: NodeProps<BuilderNode>) {
  return (
    <article className={`step-box ${selected ? 'selected' : ''} ${data.kind === 'end' ? 'end' : ''}`}>
      {data.kind === 'llm' ? <Handle type="target" position={Position.Left} /> : <Handle type="target" position={Position.Left} />}
      <div className="step-box-heading">
        <span className={data.kind === 'end' ? 'step-kind end' : 'step-kind'}>{data.kind === 'end' ? 'END' : 'AGENT'}</span>
        {data.entry ? <span className="step-entry">ENTRY</span> : null}
        {data.global ? <span className="step-global">GLOBAL</span> : null}
      </div>
      <strong>{data.title}</strong>
      <p>{data.prompt || 'No prompt configured'}</p>
      <footer><span>{data.tools} tools</span><span>{data.transitions} routes</span></footer>
      {data.kind === 'llm' ? <Handle type="source" position={Position.Right} /> : null}
    </article>
  );
}

const nodeTypes = { step: StepBox };

export function ConversationFlowBuilder({
  workflow,
  layout,
  readOnly,
  onChange,
}: {
  workflow: ConversationFlowRuntime;
  layout: Layout;
  readOnly: boolean;
  onChange: (workflow: ConversationFlowRuntime, layout: Layout) => void;
}) {
  const [selectedId, setSelectedId] = useState(workflow.entry_step);
  const [mode, setMode] = useState<'visual' | 'json'>('visual');
  const [jsonDraft, setJsonDraft] = useState(() => JSON.stringify(workflow, null, 2));
  const [jsonError, setJsonError] = useState('');
  const allSteps = [...workflow.steps, ...workflow.global_steps];
  const selected = allSteps.find((step) => step.id === selectedId);
  const globalIds = new Set(workflow.global_steps.map((step) => step.id));

  const nodes: BuilderNode[] = allSteps.map((step, index) => ({
      id: step.id,
      type: 'step',
      position: layout[step.id] ?? { x: (index % 4) * 300, y: Math.floor(index / 4) * 250 },
      data: {
        title: step.id.replaceAll('_', ' '),
        kind: step.type,
        prompt: step.type === 'llm' ? step.prompt : step.final_message ?? 'End conversation',
        entry: workflow.entry_step === step.id,
        global: globalIds.has(step.id),
        tools: step.type === 'llm' ? step.tools.length : 0,
        transitions: step.type === 'llm' ? step.transitions.length : 0,
      },
      selected: selectedId === step.id,
    }));

  const edges: Edge[] = allSteps.flatMap((step) => step.type === 'llm' ? step.transitions.map((transition, index) => ({
      id: `${step.id}:${index}:${transition.to}`,
      source: step.id,
      target: transition.to,
      label: transition.when,
      markerEnd: { type: MarkerType.ArrowClosed },
      data: { sourceId: step.id, transitionIndex: index },
      className: 'runtime-edge',
    })) : []);

  const updateStep = (id: string, update: (step: ConversationFlowRuntime['steps'][number] | ConversationFlowRuntime['global_steps'][number]) => void) => {
    const next = structuredClone(workflow);
    const step = [...next.steps, ...next.global_steps].find((item) => item.id === id);
    if (!step) return;
    update(step);
    onChange(next, layout);
  };

  const addStep = (kind: 'llm' | 'end' | 'global') => {
    const prefix = kind === 'llm' ? 'new_agent_step' : kind === 'end' ? 'end_conversation' : 'global_exit';
    let suffix = 1;
    let id = `${prefix}_${suffix}`;
    while (allSteps.some((step) => step.id === id)) id = `${prefix}_${++suffix}`;
    const next = structuredClone(workflow);
    if (kind === 'llm') next.steps.push({ id, type: 'llm', prompt: 'Describe this step task and ask one concise question.', tools: [], collect: [], transitions: [] });
    if (kind === 'end') next.steps.push({ id, type: 'end', final_message: 'Thank you. Goodbye.', terminate_call: true });
    if (kind === 'global') next.global_steps.push({ id, type: 'end', when: 'the guest clearly wants to end the conversation', final_message: 'Thank you. Goodbye.', terminate_call: true, exclude: [] });
    const nextLayout = { ...layout, [id]: { x: 80 + allSteps.length * 35, y: 520 } };
    setSelectedId(id);
    onChange(next, nextLayout);
  };

  const connect = ({ source, target }: Connection) => {
    if (!source || !target || source === target) return;
    updateStep(source, (step) => {
      if (step.type !== 'llm' || step.transitions.length >= 8) return;
      if (!step.transitions.some((transition) => transition.to === target)) step.transitions.push({ to: target, when: `the conversation is ready to move to ${target.replaceAll('_', ' ')}` });
    });
  };

  const deleteSelected = () => {
    if (!selected || selected.id === workflow.entry_step) return;
    const next = structuredClone(workflow);
    next.steps = next.steps.filter((step) => step.id !== selected.id);
    next.global_steps = next.global_steps.filter((step) => step.id !== selected.id);
    for (const step of [...next.steps, ...next.global_steps]) if (step.type === 'llm') step.transitions = step.transitions.filter((transition) => transition.to !== selected.id);
    const nextLayout = { ...layout };
    delete nextLayout[selected.id];
    setSelectedId(next.entry_step);
    onChange(next, nextLayout);
  };

  const applyJson = () => {
    try {
      const parsed = conversationFlowRuntimeSchema.parse(JSON.parse(jsonDraft));
      setJsonError('');
      onChange(parsed, layout);
      setMode('visual');
    } catch (error) {
      setJsonError(error instanceof Error ? error.message : 'Invalid conversation_flow JSON');
    }
  };

  return (
    <section className="runtime-builder" aria-label="Conversation flow step builder">
      <header className="runtime-builder-toolbar">
        <div className="builder-tabs" role="tablist" aria-label="Flow editor mode">
          <button role="tab" aria-selected={mode === 'visual'} onClick={() => setMode('visual')}>Agent steps</button>
          <button role="tab" aria-selected={mode === 'json'} onClick={() => { setJsonDraft(JSON.stringify(workflow, null, 2)); setMode('json'); }}>Flow JSON</button>
        </div>
        <div className="builder-actions">
          <button className="button secondary" onClick={() => addStep('llm')}>＋ Agent step</button>
          <button className="button secondary" onClick={() => addStep('end')}>＋ End step</button>
          <button className="button secondary" onClick={() => addStep('global')}>＋ Global exit</button>
          <span className="local-preview-pill">{readOnly ? 'Local preview' : 'Draft editor'}</span>
        </div>
      </header>
      {mode === 'json' ? (
        <div className="flow-json-editor">
          <textarea aria-label="Conversation flow JSON" value={jsonDraft} onChange={(event) => setJsonDraft(event.target.value)} spellCheck={false} />
          {jsonError ? <p className="form-error" role="alert">{jsonError}</p> : null}
          <button className="button primary" onClick={applyJson}>Apply valid JSON</button>
        </div>
      ) : (
        <div className="runtime-builder-body">
          <div className="runtime-canvas">
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              onNodeClick={(_event, node) => setSelectedId(node.id)}
              onNodeDragStop={(_event, node) => onChange(workflow, { ...layout, [node.id]: node.position })}
              onConnect={connect}
              onEdgesDelete={(deleted) => {
                const next = structuredClone(workflow);
                for (const edge of deleted) {
                  const source = [...next.steps, ...next.global_steps].find((step) => step.id === edge.source);
                  if (source?.type === 'llm') source.transitions = source.transitions.filter((_item, index) => index !== Number(edge.data?.transitionIndex));
                }
                onChange(next, layout);
              }}
              fitView
              minZoom={0.35}
              maxZoom={1.6}
              deleteKeyCode={['Backspace', 'Delete']}
            >
              <Background gap={18} size={1} />
              <MiniMap pannable zoomable />
              <Controls />
            </ReactFlow>
          </div>
          <aside className="step-inspector panel" aria-label="Selected step inspector">
            {selected ? <>
              <div className="module-toolbar"><h2>{selected.id.replaceAll('_', ' ')}</h2><span className="ready-pill">{selected.type}</span></div>
              <label>Step id<input value={selected.id} disabled /></label>
              {selected.type === 'llm' ? <>
                <label>Step prompt<textarea value={selected.prompt} onChange={(event) => updateStep(selected.id, (step) => { if (step.type === 'llm') step.prompt = event.target.value; })} /></label>
                <label>Allowed flow tools<input value={selected.tools.join(', ')} onChange={(event) => updateStep(selected.id, (step) => { if (step.type === 'llm') step.tools = event.target.value.split(',').map((item) => item.trim()).filter(Boolean); })} /></label>
                {selected.tools.length === 0 ? <p className="alert warning compact">Empty means the agent’s full configured tool set is available at runtime.</p> : null}
                <label>Collect state fields<input value={selected.collect.join(', ')} onChange={(event) => updateStep(selected.id, (step) => { if (step.type === 'llm') step.collect = event.target.value.split(',').map((item) => item.trim()).filter(Boolean); })} /></label>
                <h3>Transitions</h3>
                {selected.transitions.map((transition, index) => <label key={`${transition.to}-${index}`}>To {transition.to}<textarea value={transition.when} onChange={(event) => updateStep(selected.id, (step) => { if (step.type === 'llm' && step.transitions[index]) step.transitions[index].when = event.target.value; })} /></label>)}
              </> : <>
                <label>Final message<textarea value={selected.final_message ?? ''} onChange={(event) => updateStep(selected.id, (step) => { if (step.type === 'end') step.final_message = event.target.value; })} /></label>
                <label className="checkbox-row"><input type="checkbox" checked={selected.terminate_call} onChange={(event) => updateStep(selected.id, (step) => { if (step.type === 'end') step.terminate_call = event.target.checked; })} />Terminate call</label>
              </>}
              {'when' in selected ? <label>Global routing condition<textarea value={String(selected.when)} onChange={(event) => updateStep(selected.id, (step) => { if ('when' in step) step.when = event.target.value; })} /></label> : null}
              <button className="button danger full-width" disabled={selected.id === workflow.entry_step} onClick={deleteSelected}>Delete selected step</button>
            </> : <p className="muted">Select a step to edit its runtime configuration.</p>}
          </aside>
        </div>
      )}
    </section>
  );
}
